/**
 * SearchableDropdown — typeahead with forced selection (no free-text saves).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Check, Loader } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SearchableDropdown.css';

function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export default function SearchableDropdown({
  type,
  value,
  onSelect,
  placeholder = 'Search...',
  disabled = false,
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(!!value);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); setSelected(!!value); }, [value]);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await apiClient.get(
        `/api/classification/suggestions?type=${type}&q=${encodeURIComponent(q)}&limit=10`
      );
      setSuggestions(res.suggestions || []);
      setOpen((res.suggestions || []).length > 0);
      setActiveIdx(-1);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  }, [type]);

  const debouncedFetch = useDebounce(fetchSuggestions, 250);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setSelected(false);
    onSelect(null);
    debouncedFetch(v);
  };

  const handlePick = (item) => {
    setQuery(item.label);
    setSuggestions([]);
    setOpen(false);
    setSelected(true);
    onSelect(item);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (activeIdx >= 0 && suggestions[activeIdx]) handlePick(suggestions[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) listRef.current.children[activeIdx]?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const isInvalid = query.length > 1 && !selected;

  return (
    <div ref={containerRef} className={`sdd-container${isInvalid ? ' sdd-invalid' : ''}${selected ? ' sdd-confirmed' : ''}`}>
      <div className="sdd-input-wrap">
        {loading ? <Loader size={13} className="sdd-icon sdd-spin" /> :
         selected ? <Check size={13} className="sdd-icon sdd-icon-check" /> :
         <Search size={13} className="sdd-icon" />}
        <input
          ref={inputRef}
          className="sdd-input"
          type="text"
          value={query}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
        />
        {isInvalid && <span className="sdd-warning" title="Select from list to save">⚠</span>}
      </div>

      {isInvalid && query.length >= 2 && (
        <div className="sdd-hint">Select from the list — free-text not allowed</div>
      )}

      {open && suggestions.length > 0 && (
        <ul ref={listRef} className="sdd-list" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s.value + i}
              className={`sdd-item${i === activeIdx ? ' sdd-item-active' : ''}`}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); handlePick(s); }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <span className="sdd-item-label">{s.label}</span>
              {s.meta && <span className="sdd-item-meta">{s.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
